import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiUsers, FiCheck, FiShield, FiClock, FiMapPin, FiStar } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { SITE_URL, WHATSAPP } from '../config/seo-data';

const ease = [0.22, 1, 0.36, 1];

const vehicles = [
  {
    name: 'Mercedes V-Class',
    passengers: 'Up to 4',
    luggage: '4 large bags',
    desc: 'Perfect for families or small groups. Premium comfort with air conditioning and leather seats.',
  },
  {
    name: 'Mercedes Vito XL',
    passengers: 'Up to 6',
    luggage: '6 large bags',
    desc: 'Ideal for larger families or friend groups. Extra legroom and luggage space.',
  },
  {
    name: 'Multi-Vehicle Fleet',
    passengers: '7–50+',
    luggage: 'Unlimited',
    desc: 'Multiple coordinated vehicles for corporate events, weddings, and large parties. Seamless logistics.',
  },
];

const useCases = [
  { icon: FiUsers, title: 'Corporate Events', desc: 'Conference transfers, team retreats, and business dinners. Punctual, professional, coordinated.' },
  { icon: FiStar, title: 'Family Reunions', desc: 'Multi-generational groups with child seats, extra luggage space, and patient drivers.' },
  { icon: FiMapPin, title: 'Shore Excursions', desc: 'Cruise groups exploring Corfu together. We guarantee return before ship departure.' },
  { icon: FiClock, title: 'Airport Coordination', desc: 'Guests arriving on different flights? We track each one and coordinate pickups seamlessly.' },
];

const included = [
  'Dedicated group coordinator',
  'Real-time flight tracking for all arrivals',
  'Mercedes-Benz vehicles with A/C',
  'Child & booster seats on request',
  'Professional English-speaking drivers',
  'Luggage assistance at every stop',
  'Flexible scheduling & route changes',
  'Single point of contact for entire group',
];

const GroupTransfers = () => (
  <SEOPageLayout
    title="Lasmari Chauffeurs | Corfu Group Transfers & Minibus Hire"
    description="Group transfers in Corfu for families, corporate events, and large parties. Minibus and multi-vehicle options for airport, port, and hotel transfers."
    keywords="corfu group transfer, corfu minibus hire, corfu minibus transfer, corfu coach hire, corfu corporate transfer, corfu event transport"
    canonical={`${SITE_URL}/group-transfers`}
  >
    {/* Hero */}
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/backgrounds/kassiopi-harbor-vclass.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 45%, rgba(0,0,0,0.55), transparent 72%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="text-white/85 text-xs uppercase tracking-[0.4em] mb-6 font-light"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Everyone Arrives Together
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl text-white mb-8 leading-[1.08] tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 18px rgba(0,0,0,0.6)' }}
        >
          Group
          <br />
          <span className="font-light italic">Transfers</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.2 }}
          className="text-gray-300 text-lg lg:text-xl max-w-2xl mx-auto mb-10 font-light leading-relaxed"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Families, corporate teams, wedding parties, we coordinate comfortable transport for groups of any size across Corfu.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hello! I need a group transfer in Corfu. Can you help?')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#ba1609] text-white px-10 py-4 rounded-full font-medium hover:bg-[#8e1107] transition-all duration-300 text-base tracking-wide"
          >
            Get Group Quote
          </a>
          <Link
            to="/booking"
            className="text-white/80 hover:text-white px-8 py-4 rounded-full border border-white/20 hover:border-white/40 transition-all duration-300 text-sm tracking-wide"
          >
            Book Online
          </Link>
        </motion.div>
      </div>
    </section>

    {/* Use Cases */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Perfect For</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Groups of <span className="italic">Every Kind</span>
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {useCases.map((u, i) => (
            <motion.div
              key={u.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1, ease }}
              className="group bg-[#faf7f6] border border-black/10 rounded-2xl p-8 hover:bg-white hover:border-[#ba1609]/30 shadow-sm hover:shadow-lg transition-[color,background-color,border-color,box-shadow] duration-500 text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-[#fbeceb] border border-[#ba1609]/20 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#ba1609]/15 transition-all duration-500">
                <u.icon className="w-6 h-6 text-[#ba1609]" />
              </div>
              <h3 className="text-[#1a1012] text-lg font-medium mb-3">{u.title}</h3>
              <p className="text-[#5b4d4f] text-sm leading-relaxed font-light">{u.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Vehicle Options */}
    <section className="py-24 lg:py-32 bg-[#faf7f6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Our Fleet</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Vehicle <span className="italic">Options</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {vehicles.map((v, i) => (
            <motion.div
              key={v.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1, ease }}
              className="group bg-white border border-black/10 rounded-2xl p-8 shadow-sm hover:border-[#ba1609]/30 hover:shadow-lg transition-[color,background-color,border-color,box-shadow] duration-500"
            >
              <h3 className="text-[#1a1012] text-xl font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {v.name}
              </h3>
              <div className="w-10 h-[1px] bg-[#ba1609] mb-5" />
              <p className="text-[#5b4d4f] text-sm leading-relaxed font-light mb-6">{v.desc}</p>
              <div className="flex gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <FiUsers className="w-4 h-4 text-[#ba1609]" />
                    <span className="text-[#1a1012] text-sm font-medium">{v.passengers}</span>
                  </div>
                  <p className="text-[#5b4d4f] text-xs">Passengers</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <FiShield className="w-4 h-4 text-[#ba1609]" />
                    <span className="text-[#1a1012] text-sm font-medium">{v.luggage}</span>
                  </div>
                  <p className="text-[#5b4d4f] text-xs">Luggage</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* What's Included */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
          >
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Every Booking Includes</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#1a1012] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              Seamless
              <br /><span className="italic">Group Logistics</span>
            </h2>
            <div className="space-y-4">
              {included.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-[#fbeceb] border border-[#ba1609]/20 flex items-center justify-center shrink-0">
                    <FiCheck className="w-3 h-3 text-[#ba1609]" />
                  </div>
                  <span className="text-[#5b4d4f] text-sm font-light">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="bg-[#faf7f6] border border-black/10 rounded-2xl p-8 lg:p-10 shadow-sm"
          >
            <h3 className="text-[#1a1012] text-xl font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Why Not Just Improvise On The Day?
            </h3>
            <div className="w-10 h-[1px] bg-[#ba1609] mb-6" />
            <div className="space-y-4 text-[#5b4d4f] text-sm leading-relaxed font-light">
              <p>Walk-up airport ranks in Corfu are limited and don't take reservations. During peak season, wait times can exceed 45 minutes, multiplied across your entire group.</p>
              <p>With pre-booked group transport, every vehicle is waiting when your guests land. No scrambling, no splitting up, no confusion about where to go.</p>
              <p>One coordinator handles everything. You share your group's flight details, and we build a transport schedule that accounts for delays, luggage, and vehicle allocation.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="py-24 bg-[#ba1609]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
        >
          <p className="text-white/85 text-xs uppercase tracking-[0.4em] mb-4">Ready?</p>
          <h2 className="text-3xl lg:text-5xl font-light text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your Group <span className="italic">Sorted</span>
          </h2>
          <p className="text-white/85 text-sm font-light mb-10 max-w-lg mx-auto leading-relaxed">
            Tell us your group size, dates, and destinations. We'll put together a tailored transport plan with a single quote.
          </p>
          <a
            href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hello! I need a group transfer in Corfu. Can you help?')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-[#ba1609] px-10 py-4 rounded-full font-medium hover:bg-[#faf7f6] transition-all duration-300 text-base tracking-wide"
          >
            Get a Group Quote
          </a>
        </motion.div>
      </div>
    </section>
  </SEOPageLayout>
);

export default GroupTransfers;
